import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { billingAPI, clientAPI, serviceAPI } from '../services/api';
import AppDateField from './AppDateField';
import { useAuth } from '../contexts/AuthContext';
import { Client, Service } from '../types';

interface BillingLine {
  provider_id: number;
  billing_date: string;
  billing_line: string;
  is_transportation?: boolean;
}

// Helper function to format date without timezone conversion issues
// Parses date string directly to avoid timezone conversion
const formatDateOnly = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    // Parse the date string directly to avoid timezone conversion
    // Handle ISO format: "2024-01-15T00:00:00" or "2024-01-15T00:00:00Z" or "2024-01-15"
    const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-').map(Number);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return 'Invalid date';
    }
    
    // Create a date object using local timezone to avoid conversion issues
    const dateObj = new Date(year, month - 1, day);
    return dateObj.toLocaleDateString();
  } catch (error) {
    return 'Invalid date';
  }
};

interface BillingResponse {
  provider_id: number;
  total_records: number;
  date_range: {
    start_date: string | null;
    end_date: string | null;
  };
  billing_lines: BillingLine[];
}

const Billing: React.FC = () => {
  const { provider } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [billingData, setBillingData] = useState<BillingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingText, setBillingText] = useState<string>('');
  
  // Separate state for summary filters
  const [summaryClientId, setSummaryClientId] = useState<number | null>(null);
  const [summaryServiceCode, setSummaryServiceCode] = useState<string>('');
  const [billingSummary, setBillingSummary] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    if (provider?.provider_id) {
      fetchClients();
      fetchServices();
    }
  }, [provider]);

  const fetchClients = async () => {
    if (!provider?.provider_id) return;
    try {
      const data = await clientAPI.getClients(provider.provider_id);
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const data = await serviceAPI.getServices();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchBillingData = async () => {
    if (!provider?.provider_id) {
      setError('Provider information not available');
      return;
    }

    setLoading(true);
    setError(null);
    setBillingData(null);
    setBillingText('');

    try {
      const params: any = {};
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await billingAPI.getProviderBilling(provider.provider_id, params.start_date, params.end_date);
      setBillingData(response);
      
      // Format billing lines for easy copying (sorted from oldest to newest)
      const formattedText = response.billing_lines
        .map((line: BillingLine) => line.billing_line)
        .join('\n');
      setBillingText(formattedText);
    } catch (error: any) {
      console.error('Error fetching billing data:', error);
      setError(error.response?.data?.detail || 'Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingSummary = async () => {
    if (!provider?.provider_id) {
      return;
    }

    setLoadingSummary(true);
    try {
      const params: any = {};
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const summaryResponse = await billingAPI.getProviderBillingSummaryDetails(
        provider.provider_id, 
        params.start_date, 
        params.end_date,
        summaryClientId || undefined,
        summaryServiceCode || undefined
      );
      setBillingSummary(summaryResponse);
    } catch (summaryError) {
      console.error('Error fetching billing summary:', summaryError);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSummaryClientChange = (event: any) => {
    setSummaryClientId(event.target.value || null);
    // Don't clear billing data - summary filters are independent
  };

  const handleSummaryServiceCodeChange = (event: any) => {
    setSummaryServiceCode(event.target.value || '');
    // Don't clear billing data - summary filters are independent
  };

  const handleDateChange = (ymd: string | null, type: 'start' | 'end') => {
    if (type === 'start') {
      setStartDate(ymd);
    } else {
      setEndDate(ymd);
    }
    setBillingData(null);
    setBillingText('');
    setBillingSummary(null); // Clear summary when dates change since summary uses same dates
    setError(null);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(billingText);
  };

  if (!provider) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Provider information not available. Please log in again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          My Billing Data
        </Typography>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Options
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Provider: {provider.first_name} {provider.last_name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <AppDateField
                  label="Start Date"
                  value={startDate}
                  onChange={ymd => handleDateChange(ymd, 'start')}
                  helperText="Optional - Leave blank for all dates"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <AppDateField
                  label="End Date"
                  value={endDate}
                  onChange={ymd => handleDateChange(ymd, 'end')}
                  helperText="Optional - Leave blank for all dates"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={fetchBillingData}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Fetch Data'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {billingData && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Your Billing Data
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={`${billingData.total_records} records`} 
                    color="primary" 
                    variant="outlined" 
                  />
                  {billingData.date_range.start_date && (
                    <Chip 
                      label={`From: ${billingData.date_range.start_date}`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                  )}
                  {billingData.date_range.end_date && (
                    <Chip 
                      label={`To: ${billingData.date_range.end_date}`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Billing Lines (Click "Copy All" to copy all data to clipboard):
                </Typography>
                <Button
                  variant="outlined"
                  onClick={copyToClipboard}
                  sx={{ mb: 2 }}
                >
                  Copy All
                </Button>
              </Box>
              
              <Paper 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#f5f5f5', 
                  maxHeight: '400px', 
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {billingText || 'No billing data available'}
              </Paper>

              {/* Additional Filters for Summary */}
              {billingData && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    Filter Summary Report
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Select Client (Optional)</InputLabel>
                        <Select
                          value={summaryClientId || ''}
                          onChange={handleSummaryClientChange}
                          label="Select Client (Optional)"
                        >
                          <MenuItem value="">
                            <em>All Clients</em>
                          </MenuItem>
                          {clients.map((client) => (
                            <MenuItem key={client.client_id} value={client.client_id}>
                              {client.first_name} {client.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Select Service Code (Optional)</InputLabel>
                        <Select
                          value={summaryServiceCode}
                          onChange={handleSummaryServiceCodeChange}
                          label="Select Service Code (Optional)"
                        >
                          <MenuItem value="">
                            <em>All Service Codes</em>
                          </MenuItem>
                          {Array.from(new Set(services.map(s => s.code).filter(Boolean))).sort().map((code) => (
                            <MenuItem key={code} value={code}>
                              {code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={fetchBillingSummary}
                      disabled={loadingSummary || !provider?.provider_id}
                    >
                      {loadingSummary ? <CircularProgress size={24} /> : 'Update Summary'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Billing Summary Section */}
              {billingSummary && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Billing Summary by Service Code
                    </Typography>
                    
                    {/* Date Range */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Box sx={{ fontWeight: 'bold' }}>Start Date</Box>
                        <Box>{formatDateOnly(billingData?.date_range.start_date)}</Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ fontWeight: 'bold' }}>End Date</Box>
                        <Box>{formatDateOnly(billingData?.date_range.end_date)}</Box>
                      </Grid>
                    </Grid>

                    {/* Breakdown Table */}
                    {billingSummary.breakdown && billingSummary.breakdown.length > 0 && (
                      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 600 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', p: 2, pb: 1 }}>
                          Breakdown by Client and Service Code
                        </Typography>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Billing Date</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Service Code</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Service Title</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Units</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Billed</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {billingSummary.breakdown.map((item: any, index: number) => (
                              <TableRow 
                                key={index}
                                sx={{ 
                                  '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                              >
                                <TableCell>{item.billing_date || 'N/A'}</TableCell>
                                <TableCell>{item.client_name || `Client ${item.client_id}`}</TableCell>
                                <TableCell>{item.service_code || 'N/A'}</TableCell>
                                <TableCell>{item.service_title || 'N/A'}</TableCell>
                                <TableCell align="right">${item.rate?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell align="right">{item.total_units?.toFixed(2) || '0.00'}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${item.total_billed?.toFixed(2) || '0.00'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Overall Totals */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Overall Totals
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ fontWeight: 'bold', mb: 1 }}>Total Units</Box>
                          <Box>{billingSummary.total_units?.toFixed(2) || '0.00'}</Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ fontWeight: 'bold', mb: 1 }}>Average Rate</Box>
                          <Box>${billingSummary.average_rate?.toFixed(2) || '0.00'}</Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ fontWeight: 'bold', mb: 1 }}>Total Billed</Box>
                          <Box sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#2D4B73' }}>
                            ${billingSummary.total_billed?.toFixed(2) || '0.00'}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

export default Billing;
